/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "nesting" }] */
import { useState } from "react";
import { AffiliateOperation, AffiliateState } from "../../interfaces/affiliate";
import { affiliateError } from "../../hooks/api/admin/useAffiliates";
import { verifiedAffiliateEnvironment } from "../../hooks/api/admin/affiliateEnvironment";
import { isAffiliateTestEnvironment } from "../../utils/env";
import { affiliateSettings, launchPrepared } from "./configuration";

export function ConfigurationPanel({
  data,
  locked,
  run,
}: {
  data?: AffiliateState;
  locked: boolean;
  run: (
    operation: AffiliateOperation,
    payload: Record<string, unknown>
  ) => Promise<boolean>;
}) {
  // Only explicitly edited fields override the latest verified server settings.
  const [draft, setDraft] = useState<{
    enabled?: boolean;
    owner_floor_fees?: boolean;
    weekly_cash?: boolean;
    aevo_usd_price?: string;
  }>({});
  const [error, setError] = useState<string>();
  let configurationError: string | undefined;
  if (verifiedAffiliateEnvironment(data)) {
    try {
      affiliateSettings(data);
    } catch (caught) {
      configurationError = affiliateError(caught);
    }
  }
  const unavailable =
    locked || !verifiedAffiliateEnvironment(data) || !!configurationError;
  const enabled = draft.enabled ?? data?.enabled ?? false;
  const weeklyCash = draft.weekly_cash ?? data?.weekly_cash ?? false;
  const prepared = launchPrepared(data);
  const edit = (change: Partial<typeof draft>) =>
    setDraft((value) => ({ ...value, ...change }));

  return (
    <section>
      <h2>Program settings</h2>
      <p>Control affiliate access and weekly payments.</p>
      {(error || configurationError) && (
        <p role="alert">{error || configurationError}</p>
      )}
      {!prepared && !configurationError && (
        <p>
          Setup incomplete: pausing is available; enabling requires preparation.
          A pause preserves the existing enrollment settings.
        </p>
      )}
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (unavailable) return;
          setError(undefined);
          try {
            const current = affiliateSettings(data);
            if (
              !prepared &&
              ((!current.enabled && enabled) ||
                (!current.weekly_cash && weeklyCash))
            )
              throw new Error(
                "Complete backend preparation before enabling affiliates or weekly payments."
              );
            if (
              !current.weekly_cash &&
              weeklyCash &&
              data?.capabilities?.weekly_cash !== true
            )
              throw new Error(
                "This backend has not enabled weekly cash payments."
              );
            if (
              !prepared &&
              !(
                (current.enabled && !enabled) ||
                (current.weekly_cash && !weeklyCash)
              )
            )
              throw new Error(
                "Complete backend preparation before saving public enrollment settings."
              );
            const price =
              draft.aevo_usd_price?.trim() || current.aevo_usd_price;
            if (!/^[0-9]+(\.[0-9]+)?$/.test(price))
              throw new Error("Enter a nonnegative decimal AEVO price.");
            if (
              await run("configure", {
                ...current,
                enabled,
                weekly_cash: weeklyCash,
                // Legacy policies may reject open enrollment. Keep the ability
                // to pause them without silently changing their policies.
                ...(prepared ? { enrollment_mode: "open", allowlist: [] } : {}),
                aevo_usd_price: price,
                ...(data?.capabilities?.owner_floor_fees &&
                (draft.owner_floor_fees ||
                  current.owner_fee_policy === "tier_floor_v1")
                  ? { owner_fee_policy: "tier_floor_v1" }
                  : {}),
              })
            )
              setDraft({});
          } catch (caught) {
            setError(affiliateError(caught));
          }
        }}
      >
        <fieldset disabled={unavailable}>
          <label style={{ flexDirection: "row", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={enabled}
              disabled={!prepared && !data?.enabled}
              onChange={(event) => edit({ enabled: event.target.checked })}
            />
            Enable affiliates
          </label>
          <label style={{ flexDirection: "row", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={weeklyCash}
              disabled={
                !data?.weekly_cash &&
                (!prepared || data?.capabilities?.weekly_cash !== true)
              }
              onChange={(event) => edit({ weekly_cash: event.target.checked })}
            />
            Enable weekly cash payments
          </label>
          {data?.capabilities?.owner_floor_fees && (
            <label style={{ flexDirection: "row", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={
                  draft.owner_floor_fees ??
                  data.owner_fee_policy === "tier_floor_v1"
                }
                disabled={data.owner_fee_policy === "tier_floor_v1"}
                onChange={(event) =>
                  edit({ owner_floor_fees: event.target.checked })
                }
              />
              Apply tier floor fees to affiliate wallets
            </label>
          )}
          {isAffiliateTestEnvironment() && (
            <label>
              Test AEVO price (USD)
              <input
                inputMode="decimal"
                pattern="[0-9]+(\.[0-9]+)?"
                value={draft.aevo_usd_price ?? ""}
                placeholder={data?.aevo_usd_price || "Unchanged"}
                onChange={(event) =>
                  edit({ aevo_usd_price: event.target.value })
                }
              />
            </label>
          )}
          <button type="submit">Save program settings</button>
        </fieldset>
      </form>
      {data?.capabilities?.owner_floor_fees && (
        <p>
          Tier floor fees apply to future trades only. Once enabled, this policy
          cannot be turned off here.
        </p>
      )}
      <p>
        USDC becomes payable after a seven-day hold. Turning payments off does
        not cancel transfers already submitted.
      </p>
      {isAffiliateTestEnvironment() && (
        <p>
          The supplied AEVO price is a test value for historical obligations,
          not a market-price source.
        </p>
      )}
    </section>
  );
}
